<template>
  <div class="compare-columns">
    <div v-if="records.length === 0" class="empty-state">
      <i class="fas fa-clipboard-list" aria-hidden="true"></i>
      <h2>Geen projecten geselecteerd</h2>
      <p>Ga terug naar de lijstweergave en selecteer projecten om te vergelijken</p>
    </div>

    <div v-else class="row g-3">
      <div 
        v-for="record in records" 
        :key="record.id" 
        :class="getColumnClass()"
      >
        <div class="compare-column">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <h2 class="h5 mb-0 text-truncate flex-grow-1" :title="formatValue(record[compareFields[0]])">
              {{ formatValue(record[compareFields[0]]) }}
            </h2>
            <button type="button" class="btn btn-sm btn-outline-danger ms-2 flex-shrink-0"
                    @click="$emit('remove', record.id)"
                    :aria-label="'Verwijder ' + formatValue(record[compareFields[0]]) + ' uit de vergelijking'">
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <!-- Het eerste vergelijkveld staat al als kop boven de kolom; hier
               nog eens meenemen levert elke kolom een dubbele regel op. -->
          <div v-for="field in detailVelden" :key="field" class="field-row">
            <div class="field-label text-truncate" :title="getFieldLabel(field)">
              {{ getFieldLabel(field) }}
            </div>
            <div class="field-value text-truncate" :title="formatValue(record[field])">
              {{ formatValue(record[field]) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

export default defineComponent({
  name: 'CompareColumns',
  props: {
    records: {
      type: Array as PropType<any[]>,
      required: true
    },
    compareFields: {
      type: Array as PropType<string[]>,
      required: true
    },
    fieldLabels: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({})
    }
  },
  emits: ['remove'],
  computed: {
    detailVelden(): string[] {
      return this.compareFields.slice(1);
    }
  },
  methods: {
    getColumnClass(): string {
      const count = this.records.length;
      if (count === 1) return 'col-12';
      if (count === 2) return 'col-md-6';
      if (count === 3) return 'col-md-4';
      if (count === 4) return 'col-md-3 col-lg-3';
      if (count === 5) return 'col-md-4 col-lg-2-4';
      if (count === 6) return 'col-md-4 col-lg-2';
      if (count <= 8) return 'col-md-3 col-lg-2 col-xl-1-5';
      // 9-10+ kolommen
      return 'col-md-3 col-lg-2 col-xl-1-2';
    },

    formatValue(value: any): string {
      if (value === null || value === undefined) return '-';
      if (Array.isArray(value)) return value.join(', ');
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    },

    getFieldLabel(fieldName: string): string {
      return this.fieldLabels[fieldName] || fieldName;
    }
  }
});
</script>

<style scoped>
.compare-columns {
  min-height: 400px;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #6c757d;
}

.empty-state i {
  font-size: 4rem;
  opacity: 0.3;
  margin-bottom: 1rem;
  display: block;
}

.compare-column {
  border: 2px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
  background: white;
  height: 100%;
  transition: all 0.2s;
}

.compare-column:hover {
  border-color: #42b983;
  box-shadow: 0 4px 12px rgba(66, 185, 131, 0.2);
}

.compare-column h2 {
  color: var(--primary, #0f6c65);
  font-weight: 600;
  border-bottom: 2px solid var(--primary, #0f6c65);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: help;
}

.field-row {
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  min-height: 3.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.field-row:last-child {
  border-bottom: none;
}

.field-label {
  font-weight: 600;
  color: #6c757d;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: help;
  line-height: 1.2rem;
  height: 1.2rem;
}

.field-value {
  color: #212529;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: help;
  line-height: 1.5rem;
  height: 1.5rem;
}

/* Custom kolom breedtes voor 5+ kolommen */
@media (min-width: 992px) {
  .col-lg-2-4 {
    flex: 0 0 20%;
    max-width: 20%;
  }
}

@media (min-width: 1200px) {
  .col-xl-1-5 {
    flex: 0 0 12.5%;
    max-width: 12.5%;
  }
  
  .col-xl-1-2 {
    flex: 0 0 10%;
    max-width: 10%;
  }
}

/* Zorg voor minimale breedte op kleinere schermen */
@media (max-width: 1199px) {
  .compare-column {
    min-width: 200px;
  }
}

@media (max-width: 768px) {
  .compare-column {
    margin-bottom: 1rem;
  }
}
</style>









